import React, { useState, useEffect } from "react";
import { Drawer } from "vaul";
import { Check, PawPrint, Loader2, AlertCircle, ArrowRightLeft } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const CheckInDrawer = ({ open, onOpenChange, park, dogs, activeCheckIns = [], onSuccess }) => {
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Dogs already checked in AT THIS PARK
  const hereByDogId = React.useMemo(() => {
    const map = {};
    (activeCheckIns || []).forEach((c) => {
      if (c.park_id === park?.id) map[c.dog_id] = c;
    });
    return map;
  }, [activeCheckIns, park?.id]);

  // Dogs checked in AT ANOTHER PARK
  const elsewhereByDogId = React.useMemo(() => {
    const map = {};
    (activeCheckIns || []).forEach((c) => {
      if (c.park_id !== park?.id) map[c.dog_id] = c;
    });
    return map;
  }, [activeCheckIns, park?.id]);

  const selectableDogs = dogs.filter((d) => !hereByDogId[d.id]);
  const alreadyHereDogs = dogs.filter((d) => hereByDogId[d.id]);
  const allAlreadyHere = dogs.length > 0 && selectableDogs.length === 0;

  // Pre-select only dogs NOT currently here when opening
  useEffect(() => {
    if (open) {
      setSelected(selectableDogs.map((d) => d.id));
      setSuccess(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dogs, activeCheckIns]);

  const toggle = (id) => {
    if (hereByDogId[id]) return; // locked
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  const submit = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one dog");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkins", { park_id: park.id, dog_ids: selected });
      track("check_in_completed", {
        park_id: park.id,
        park_name: park.name,
        dog_count: selected.length,
        moved_from_count: data.moved_from?.length || 0,
      });
      if (data.moved_from?.length) {
        data.moved_from.forEach((m) =>
          toast(`${m.dog_name} was moved from ${m.park_name}`, { icon: "↪" })
        );
      }
      setSuccess(true);
      // Pass the dog names of THIS check-in to the parent so the
      // post-check-in share prompt can personalize its share text
      // ("with Bella & Max!"). We resolve names client-side from the
      // currently-rendered `dogs` list — the API only returns IDs.
      // Also forward `user_stats` so the share prompt can render the
      // "🐾 That's visit #X!" / "🎉 New park unlocked!" stat lines
      // without an extra round-trip. `checkinIds` powers the optional
      // "Add a note about your visit" textarea in the share prompt
      // (PATCH /checkins/{id}/note on save) — multi-dog check-ins
      // emit one row per dog, so we forward all of them and the
      // share prompt fans the same note out to each.
      const successDogNames = dogs
        .filter((d) => selected.includes(d.id))
        .map((d) => d.name);
      const checkinIds = (data.checkins || []).map((c) => c.id).filter(Boolean);
      setTimeout(() => {
        onOpenChange(false);
        onSuccess?.({
          dogNames: successDogNames,
          userStats: data.user_stats || null,
          checkinIds,
        });
      }, 1400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-brand-ink/40 z-50" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 mt-24 flex h-auto flex-col rounded-t-3xl bg-white border-t border-brand-border-warm">
          <Drawer.Title className="sr-only">Check in at {park?.name}</Drawer.Title>
          <div className="mx-auto w-12 h-1.5 rounded-full bg-brand-border-warm mt-3 mb-2" />
          <div className="p-6 max-w-xl mx-auto w-full" data-testid="checkin-drawer">
            {success ? (
              <div className="py-10 flex flex-col items-center text-center animate-fade-in-up">
                <div className="w-16 h-16 rounded-full bg-brand-forest text-white flex items-center justify-center mb-4">
                  <Check className="w-8 h-8" strokeWidth={3} />
                </div>
                <h3 className="font-heading text-2xl font-bold text-brand-ink">You're checked in!</h3>
                <p className="text-sm text-brand-ink/60 mt-2">
                  Your check-in at {park?.name} will expire in 2 hours.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-1">
                  <span className="w-10 h-10 rounded-full bg-brand-light-green text-brand-forest flex items-center justify-center">
                    <PawPrint className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-ink/50 font-semibold">Check in at</div>
                    <div className="font-heading text-lg font-semibold text-brand-ink">{park?.name}</div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-sm font-semibold text-brand-ink mb-2">Which dogs are with you?</div>
                  {dogs.length === 0 ? (
                    <p className="text-sm text-brand-ink/60 bg-brand-light-green p-3 rounded-xl">
                      You haven't added a dog yet. Add one from your profile to check in.
                    </p>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {dogs.map((d) => {
                          const lockedHere = !!hereByDogId[d.id];
                          const movingFrom = elsewhereByDogId[d.id];
                          const on = selected.includes(d.id);
                          return (
                            <button
                              key={d.id}
                              data-testid={`checkin-dog-${d.id}`}
                              onClick={() => toggle(d.id)}
                              disabled={lockedHere}
                              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all active:scale-95 flex items-center gap-1.5 ${
                                lockedHere
                                  ? "bg-brand-light-green text-brand-forest/70 border-brand-forest/30 cursor-not-allowed"
                                  : on
                                  ? "bg-brand-forest text-white border-brand-forest"
                                  : "bg-white text-brand-ink border-brand-border-warm hover:border-brand-forest"
                              }`}
                              title={lockedHere ? `${d.name} is already checked in here` : ""}
                            >
                              {lockedHere && <Check className="w-3.5 h-3.5" />}
                              {d.name} · {d.breed}
                              {movingFrom && !lockedHere && (
                                <ArrowRightLeft className="w-3 h-3 opacity-70" />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {alreadyHereDogs.length > 0 && (
                        <div
                          data-testid="already-here-message"
                          className="mt-3 flex items-start gap-2 text-sm bg-brand-light-green/70 text-brand-forest px-3 py-2 rounded-xl"
                        >
                          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>
                            {alreadyHereDogs.map((d) => d.name).join(", ")}
                            {alreadyHereDogs.length === 1 ? " is" : " are"} already checked in here!
                          </span>
                        </div>
                      )}

                      {Object.keys(elsewhereByDogId).length > 0 && selected.some((id) => elsewhereByDogId[id]) && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-brand-ink/70 bg-brand-cream px-3 py-2 rounded-xl border border-brand-border-warm">
                          <ArrowRightLeft className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-forest" />
                          <span>
                            {selected
                              .filter((id) => elsewhereByDogId[id])
                              .map((id) => {
                                const dog = dogs.find((x) => x.id === id);
                                const prev = elsewhereByDogId[id];
                                return `${dog?.name} will be moved from ${prev.park_name}`;
                              })
                              .join(" · ")}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button
                  data-testid="checkin-submit-btn"
                  onClick={submit}
                  disabled={submitting || dogs.length === 0 || allAlreadyHere || selected.length === 0}
                  className="w-full mt-6 bg-brand-forest text-white rounded-full py-3.5 font-semibold text-base hover:bg-brand-forest-dark transition-colors active:scale-[0.98] shadow-warm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {allAlreadyHere ? "All dogs already checked in" : "Check in now"}
                </button>
                <p className="text-center text-xs text-brand-ink/50 mt-3">
                  Auto-expires after 2 hours · One park at a time per dog
                </p>
              </>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default CheckInDrawer;
